import i18next from "i18next";
import { useContext } from "react";
import { useMutation, useQueryClient } from "react-query";

import { useClient } from "utils/client";
import { ControlContext } from "context/Controls";

import { getAIRiskApi } from "../api";
import { AIDraftRisk, AIHazardShell } from "../types";
import { mergeHazardIntoSessionCache } from "./sessionCache";

interface SaveAIHazardDraftVariables {
  hazardId: string;
  draft: AIDraftRisk;
  isAutoSave?: boolean;
}

// Persists the Phase-B working copy: PATCH /hazards/:hazardId { draft }.
// Used by the editor's autosave and explicit saves; autosave success reuses
// the existing notification.saving.* keys (status-quo editor parity).
export const useSaveAIHazardDraft = (projectId: string) => {
  const client = useClient();
  const queryClient = useQueryClient();
  const { displayNotification } = useContext(ControlContext);

  return useMutation<AIHazardShell, Error, SaveAIHazardDraftVariables>(
    ({ hazardId, draft }) =>
      getAIRiskApi(client).updateHazard(projectId, hazardId, { draft }),
    {
      onSuccess: (hazard, { isAutoSave }) => {
        mergeHazardIntoSessionCache(queryClient, projectId, hazard);

        if (isAutoSave) {
          displayNotification!(
            i18next.t("notification.saving.autosave_success"),
            "",
            "success"
          );

          return;
        }

        displayNotification!(
          i18next.t("notification.saving.generic_success"),
          ""
        );
      },
      onError: (error) =>
        displayNotification!(
          i18next.t("risks.ai.errors.saveFailed"),
          error.message,
          "error"
        ),
    }
  );
};
